import { faLocationCrosshairs, faLocationCrosshairsSlash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Drawer, notification } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMap } from "react-map-gl/mapbox";
import GlobalStoreContext from "../../../context/GlobalStoreContext";
import Helper from "../../../Helper";
import { Area } from "../../../models/Area";
import { Dropoff } from "../../../models/Dropoff";
import { GateToDropoffRoute } from "../../../models/GateToDropoffRoute";
import { HaulRoute } from "../../../models/HaulRoute";
import { WaypointStep } from "../../../models/WaypointStep";
import { checkApproachingWaypoint, checkDepartingWaypoint } from "../../Helper/WaypointGuidance";
import DamnMap from "../damnMap";
import EnableLocationServices from "../enableLocationServices";
import WaypointArrivalModal from "./WaypointArrivalModal";
import WaypointGuidanceStep from "./WaypointGuidanceStep";

interface WaypointGuidanceProps {
    active: boolean;
    cancel: () => void;
    route: Area | HaulRoute | GateToDropoffRoute;
    dropoff?: Dropoff;
}

const WaypointGuidance: React.FC<WaypointGuidanceProps> = (props) => {
    const context = React.useContext(GlobalStoreContext);
    const waypointMapObject = props.route.getMapObject(context);
    const dropOffMapObject = props.dropoff ? props.dropoff.getMapObject(context) : null;
    const [activeStep, setActiveStep] = useState<number>(1);
    const [userTrackingModalOpen, setUserTrackingModalOpen] = React.useState<boolean>(false);
    const [showWaypointArrivalModal, setShowWaypointArrivalModal] = React.useState<boolean>(false);
    const [currentStage, setCurrentStage] = useState<"approaching" | "departing">("approaching");
    const [distanceToNextWaypoint, setDistanceToNextWaypoint] = useState<number>(0);

    const visibleSteps = useMemo(
        () =>
            (props.route.waypointDirection?.waypointSteps ?? [])
                .filter((step) => !step.hidden)
                .sort((a, b) => a.stepNumber - b.stepNumber),
        [props.route.waypointDirection]
    );

    const { waypointMap } = useMap();

    // Wakelock prevents phone from going to sleep during waypoint guidance
    const wakeLockRef = useRef<WakeLockSentinel | null>(null);

    const hasWakeLock = (
        nav: Navigator
    ): nav is Navigator & { wakeLock: { request(type: "screen"): Promise<WakeLockSentinel> } } => {
        return "wakeLock" in nav;
    };

    const requestWakeLock = async () => {
        if (!hasWakeLock(navigator)) {
            // Wake Lock API not supported; avoid calling into unsupported API
            return;
        }

        try {
            const wakeLock = await navigator.wakeLock.request("screen");
            // Store the wake lock in a ref so it persists across renders
            wakeLockRef.current = wakeLock;
        } catch (err) {
            // wakeLock failed
        }
    };
    const releaseWakeLock = async () => {
        if (wakeLockRef.current) {
            wakeLockRef.current
                .release()
                .catch(() => {
                    // Ignoring release errors since wake lock is a best-effort feature
                })
                .finally(() => {
                    wakeLockRef.current = null;
                });
        }
    };

    useEffect(() => {
        context.setCurrentWaypointStep(visibleSteps[activeStep - 1] || null);
    }, [visibleSteps, activeStep, props.active]);

    useEffect(() => {
        if (props.active) {
            requestWakeLock();
            // Timeout ensures that the transition has completed and the div is rendered
            // This prevents map resizing issues

            setTimeout(() => {
                resizeMap(0);
            }, 700);
        }
        return () => {
            setActiveStep(1);
            context.setUserLocation(null);
            context.setCurrentWaypointStep(null);
            releaseWakeLock();
        };
    }, [props.active]);

    const resizeMap = (retryCount = 0) => {
        if (retryCount > 10) {
            return;
        }
        if (waypointMap?.getMap()) {
            waypointMap.resize();
        } else {
            setTimeout(() => resizeMap(retryCount + 1), 500);
        }
    };

    useEffect(() => {
        if (props.active) {
            // Load modal to prompt user for location after the screen has been active for 1 second
            setTimeout(() => {
                setUserTrackingModalOpen(true);
            }, 1500);
        }
    }, [props.active]);

    useEffect(() => {
        if (!props.active) {
            return;
        }
        document.getElementById(activeStep.toString())?.scrollIntoView({ behavior: "smooth" });
    }, [props.active, activeStep]);

    useEffect(() => {
        // Ensure that when the active step changes (e.g. via manual navigation),
        // we always start that step in the "approaching" stage.
        setCurrentStage("approaching");
    }, [activeStep]);

    const geolocateUser = () => {
        try {
            context.geoControlRef.current?.trigger();
        } catch (e) {
            notification.error({
                message: "Error accessing location",
                description:
                    "There was an error accessing your location. Please ensure that location services are enabled for this site in your browser settings.",
            });
            return;
        }
        setUserTrackingModalOpen(false);
    };

    const approachingLogic = (waypointStep: WaypointStep, userLocation: GeolocationCoordinates) => {
        if (!userLocation || !waypointStep.coordinates) {
            return;
        }

        const waypointResult = checkApproachingWaypoint(
            [userLocation.longitude, userLocation.latitude],
            waypointStep.coordinates!
        );
        if (waypointResult.isWithinProximity && waypointStep === visibleSteps.at(-1)) {
            setShowWaypointArrivalModal(true);
            return;
        }

        if (waypointResult.isWithinProximity) {
            setCurrentStage("departing");
            setDistanceToNextWaypoint(0);
        } else {
            setDistanceToNextWaypoint(waypointResult.distanceMeters);
        }
    };

    const departingLogic = (waypointStep: WaypointStep, userLocation: GeolocationCoordinates) => {
        if (!userLocation || !waypointStep.coordinates) {
            return;
        }
        const hasDeparted = checkDepartingWaypoint(
            [userLocation.longitude, userLocation.latitude],
            waypointStep.coordinates!
        );
        if (hasDeparted) {
            setCurrentStage("approaching");
            setActiveStep(activeStep + 1);
        }
    };

    const checkGeolocateNextStep = (userLocation: GeolocationCoordinates | null) => {
        if (!userLocation) {
            return;
        }
        const currentWaypointStep = visibleSteps[activeStep - 1];
        if (!currentWaypointStep?.coordinates) {
            return;
        }

        if (currentStage === "approaching") {
            approachingLogic(currentWaypointStep, userLocation);
            return;
        } else if (currentStage === "departing") {
            departingLogic(currentWaypointStep, userLocation);
            return;
        }
    };

    useEffect(() => {
        checkGeolocateNextStep(context.userLocation);
    }, [context.userLocation, activeStep, currentStage]);

    const mapComponent = useMemo(() => {
        return (
            <DamnMap
                mapId="waypointMap"
                mapObjects={[waypointMapObject, ...(dropOffMapObject ? [dropOffMapObject] : [])]}
                route={props.route}
                currentStep={activeStep}
                geoControlRef={context.geoControlRef}
                setUserLocation={context.setUserLocation}
            />
        );
    }, [
        waypointMapObject,
        dropOffMapObject,
        props.route,
        activeStep,
        props.active,
        context.geoControlRef,
        context.setUserLocation,
    ]);

    return (
        <Drawer
            placement="top"
            open={props.active}
            onClose={props.cancel}
            zIndex={999}
            styles={{ body: { margin: 0, padding: 0 } }}
            closable={false}
            height="100%"
        >
            <div
                className={Helper.classNames(
                    "h-full flex flex-col relative transition-all duration-700 overflow-hidden bg-plot-white backdrop-blur-md z-999"
                )}
            >
                <div className="flex-1 h-full relative">
                    {waypointMapObject ? (
                        mapComponent
                    ) : (
                        <div className="bg-linear-to-r from-blue-400 to-green-600 w-full h-full" />
                    )}
                </div>

                <div className="flex flex-nowrap overflow-x-hidden snap-x snap-mandatory pb-2">
                    {visibleSteps.map((step, idx) => (
                        <div key={step.id} id={(idx + 1).toString()} className="w-full shrink-0 snap-center">
                            <WaypointGuidanceStep
                                stepIndex={idx + 1}
                                totalSteps={visibleSteps.length}
                                showPicture={activeStep === idx + 1}
                                waypointStep={step}
                                setStep={setActiveStep}
                                distanceToNextWaypoint={distanceToNextWaypoint}
                                hasUserLocation={!!context.userLocation}
                            />
                        </div>
                    ))}
                </div>

                <div className="absolute right-0 mr-2.5 mt-2.5 flex gap-2 z-999">
                    <button
                        onClick={geolocateUser}
                        className={Helper.classNames(
                            context.userLocation ? "flowing-gradient text-white" : "bg-plot-white text-black",
                            "p-2 rounded-md gap-2 shadow-lg hover:scale-105 transition-transform"
                        )}
                    >
                        <FontAwesomeIcon
                            icon={context.userLocation ? faLocationCrosshairsSlash : faLocationCrosshairs}
                        />{" "}
                        {context.userLocation ? "Stop Using Location" : "Use my Location"}
                    </button>
                    <button
                        type="button"
                        className="cursor-pointer bg-red-500 p-2 text-white rounded-md hover:bg-red-800 hover:scale-110 transition-all duration-300"
                        onClick={props.cancel}
                    >
                        Exit
                    </button>
                </div>
                <EnableLocationServices
                    open={userTrackingModalOpen}
                    toggleOpen={() => setUserTrackingModalOpen(!userTrackingModalOpen)}
                    handleGeolocateAllow={geolocateUser}
                />
                <WaypointArrivalModal
                    isOpen={showWaypointArrivalModal}
                    onClose={() => {
                        setShowWaypointArrivalModal(false);
                        props.cancel();
                    }}
                />
            </div>
        </Drawer>
    );
};
export default WaypointGuidance;
